// Plain-text view of a markdown string, for places that can't render formatting:
// search-card snippet, `<meta name="description">`, og:description, email previews.
//
// Goals:
//   - Asterisks / underscores / hashes / list markers / link syntax all disappear.
//   - The visible *text* survives intact, including the link label (not the URL).
//   - Line structure is preserved enough for `split("\n")[0]` to still mean
//     "the first paragraph of the bio" downstream.
//
// This intentionally avoids pulling `remark` into client bundles — a small
// regex pass is enough for the narrow subset of markdown we accept in bios
// (## headings, **bold**, *italic*, [text](url), - / 1. lists). If we ever
// expand the grammar (tables, code fences, blockquotes), revisit this.
export function stripMarkdown(input: string | null | undefined): string {
  if (!input) return "";
  let s = input;

  // Fenced code blocks. Not part of our bio grammar (renderer doesn't allow
  // <pre>/<code>), but if a pro pastes one we don't want the backticks or the
  // verbatim contents leaking into snippets / meta description.
  s = s.replace(/```[\s\S]*?```/g, "");

  // Inline code: `like this` → `like this` (drop the backticks, keep the text).
  s = s.replace(/`([^`]+)`/g, "$1");

  // ATX headings: leading `##`/`###` etc. → keep the heading text only.
  s = s.replace(/^#{1,6}\s+/gm, "");

  // Links `[label](url)` → `label`. URLs disappear.
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1");

  // Bold + italic markers. Run bold before italic so `***x***` (bold-italic)
  // collapses cleanly in two passes instead of leaving a stray asterisk.
  s = s.replace(/\*\*([^*]+)\*\*/g, "$1");
  s = s.replace(/__([^_]+)__/g, "$1");
  s = s.replace(/\*([^*]+)\*/g, "$1");
  s = s.replace(/_([^_]+)_/g, "$1");

  // List markers at line start: `- `, `* `, `+ `, `1. `, `12. `.
  s = s.replace(/^\s*([-*+]|\d+\.)\s+/gm, "");

  // Collapse 3+ blank lines that the strips above may have created.
  s = s.replace(/\n{3,}/g, "\n\n");

  return s.trim();
}

// ── Live syntax highlighting (editor overlay) ────────────────────────────────
//
// Tokenize a markdown string into a flat run of tagged segments for the
// MarkdownEditor's syntax-highlighting overlay. Critically: **every character
// of the input survives in the output runs**, in order. Markers (`**`, `##`,
// `[`, `]`, `(`, `)`) are emitted as their own runs tagged `marker`; emphasised
// content is tagged `bold` / `italic` / `link-label`. The overlay renders these
// as styled <span>s inside a <pre>; same monospace font + identical padding +
// identical wrap rules as the textarea, so wrap parity holds character-for-
// character. If a character is missing or duplicated here, the caret will drift
// off its glyph in the visible overlay.
//
// Patterns recognised mirror the editor's grammar — keep this in sync with the
// allowed-elements list in `MarkdownBio.tsx`. Anything we don't recognise
// passes through as `text` (no styling), so the overlay degrades gracefully.

export type MarkdownToken =
  | "text"
  | "marker"
  | "heading-text"
  | "bold"
  | "italic"
  | "link-label"
  | "list-marker";

export interface MarkdownRun {
  type: MarkdownToken;
  text: string;
}

// Composite regex with global + multiline. Order matters when a character could
// belong to two patterns — bold (`**`) must beat italic (`*`); heading (`## `)
// only at line start. Patterns are intentionally non-greedy and bounded to a
// single line (no `\n` inside the capture) so an unmatched marker doesn't eat
// the rest of the document.
const TOKEN_RE =
  /(^#{1,6}\s+)|(^[-*+]\s+)|(\*\*[^*\n]+\*\*)|(\*[^*\n]+\*)|(_[^_\n]+_)|(\[[^\]\n]+\]\([^)\n]+\))/gm;

export function tokenizeMarkdown(input: string): MarkdownRun[] {
  const runs: MarkdownRun[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  TOKEN_RE.lastIndex = 0;

  while ((m = TOKEN_RE.exec(input)) !== null) {
    if (m.index > last) {
      runs.push({ type: "text", text: input.slice(last, m.index) });
    }
    const match = m[0];

    if (m[1] !== undefined) {
      // Heading: emit the `##` + spaces as marker, then the rest of the line
      // up to the next `\n` as `heading-text` so the overlay can bold it.
      runs.push({ type: "marker", text: match });
      const lineEnd = input.indexOf("\n", m.index + match.length);
      const stop = lineEnd === -1 ? input.length : lineEnd;
      if (stop > m.index + match.length) {
        runs.push({
          type: "heading-text",
          text: input.slice(m.index + match.length, stop),
        });
      }
      last = stop;
      TOKEN_RE.lastIndex = stop;
      continue;
    }

    if (m[2] !== undefined) {
      runs.push({ type: "list-marker", text: match });
    } else if (m[3] !== undefined) {
      runs.push({ type: "marker", text: "**" });
      runs.push({ type: "bold", text: match.slice(2, -2) });
      runs.push({ type: "marker", text: "**" });
    } else if (m[4] !== undefined) {
      runs.push({ type: "marker", text: "*" });
      runs.push({ type: "italic", text: match.slice(1, -1) });
      runs.push({ type: "marker", text: "*" });
    } else if (m[5] !== undefined) {
      runs.push({ type: "marker", text: "_" });
      runs.push({ type: "italic", text: match.slice(1, -1) });
      runs.push({ type: "marker", text: "_" });
    } else if (m[6] !== undefined) {
      // [label](url) → split into `[`, label, `](`, url, `)`.
      const close = match.indexOf("](");
      runs.push({ type: "marker", text: "[" });
      runs.push({ type: "link-label", text: match.slice(1, close) });
      runs.push({ type: "marker", text: "](" });
      runs.push({ type: "marker", text: match.slice(close + 2, -1) });
      runs.push({ type: "marker", text: ")" });
    }
    last = m.index + match.length;
  }

  if (last < input.length) {
    runs.push({ type: "text", text: input.slice(last) });
  }
  return runs;
}
